import { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import useWakeLock from "@/hooks/useWakeLock";
import { extractTimer } from "@/lib/timers";
import StepTimer from "@/components/StepTimer";
import { X, ChevronLeft, ChevronRight, ListChecks, Volume2, VolumeX } from "lucide-react";

/** Extract numbered/plain step lines from the '## Steps' (or similar) section. */
function extractSteps(md = "") {
  const lines = md.split("\n");
  const out = [];
  let inSteps = false;
  for (const raw of lines) {
    const h = /^#{1,3}\s+(.+?)\s*#*\s*$/.exec(raw);
    if (h) {
      inSteps = /step|instruction|method|direction/i.test(h[1]);
      continue;
    }
    if (inSteps) {
      const t = raw.trim();
      if (!t) continue;
      out.push(t.replace(/^(\d+\.\s*|[*\-+]\s*)/, "").replace(/[*_`]/g, ""));
    }
  }
  return out;
}

/**
 * Hands-free Cook Mode. Full-screen overlay that flips one step at a time
 * with big taps. Wake lock is held while active. Left/Right arrows and
 * on-screen tap zones both work.
 */
export default function CookMode({ idea, open, onClose }) {
  const rawSteps = useMemo(() => extractSteps(idea?.context || ""), [idea]);
  const steps = useMemo(() => rawSteps.map((s) => extractTimer(s)), [rawSteps]);
  const [i, setI] = useState(0);
  const supportsSpeech = typeof window !== "undefined" && "speechSynthesis" in window;
  const [voice, setVoice] = useState(false);

  useWakeLock(open);

  // Reset to first step whenever the overlay opens
  useEffect(() => { if (open) setI(0); }, [open, idea?.id]);

  const speak = (text) => {
    if (!supportsSpeech || !text) return;
    try {
      window.speechSynthesis.cancel();
      const u = new window.SpeechSynthesisUtterance(text);
      window.speechSynthesis.speak(u);
    } catch {}
  };

  // Speak the current step when voice is on and step/open changes
  useEffect(() => {
    if (!supportsSpeech) return;
    if (!open || !voice) return;
    speak(steps[i]?.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, voice, i, steps, supportsSpeech]);

  // Cancel any speech on close/unmount or when voice toggled off
  useEffect(() => {
    if (!supportsSpeech) return;
    if (!open || !voice) {
      try { window.speechSynthesis.cancel(); } catch {}
    }
    return () => { try { window.speechSynthesis.cancel(); } catch {} };
  }, [open, voice, supportsSpeech]);

  // Keyboard nav
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") setI((n) => Math.min(steps.length - 1, n + 1));
      if (e.key === "ArrowLeft") setI((n) => Math.max(0, n - 1));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, steps.length]);

  if (!open || !idea) return null;

  const pct = steps.length > 0 ? Math.round(((i + 1) / steps.length) * 100) : 0;
  const canPrev = i > 0;
  const canNext = i < steps.length - 1;
  const step = steps[i]?.text || "";
  const stepSeconds = steps[i]?.seconds ?? null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-[#FBF8F1] flex flex-col"
      role="dialog"
      aria-modal="true"
      data-testid="cook-mode"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#B79256]/25">
        <div>
          <div className="accent-label text-[#B79256]" data-testid="cook-progress">
            step {steps.length > 0 ? i + 1 : 0} of {steps.length}
          </div>
          <div className="font-serif-display text-xl text-[#2A2823] leading-tight mt-0.5 max-w-[70vw] truncate">
            {idea.title}
          </div>
        </div>
        <button
          type="button"
          data-testid="cook-exit"
          onClick={onClose}
          className="p-3 rounded-full hover:bg-[#B79256]/15 text-[#2A2823]/70 hover:text-[#2A2823] transition-colors"
          aria-label="Exit cook mode"
        >
          <X className="w-5 h-5" strokeWidth={1.75} />
        </button>
      </div>

      {/* Voice toggle strip */}
      {supportsSpeech && steps.length > 0 && (
        <div className="px-5 py-2 border-b border-[#B79256]/15 flex items-center gap-2 justify-end bg-white/40">
          <button
            type="button"
            data-testid="cook-voice-toggle"
            onClick={() => setVoice((v) => !v)}
            aria-pressed={voice}
            className={`accent-label inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border transition-colors ${
              voice
                ? "bg-[#4E5A3D] text-[#FBF8F1] border-[#4E5A3D] hover:bg-[#3E4830]"
                : "bg-white text-[#2A2823] border-[#B79256]/40 hover:bg-[#B79256]/12"
            }`}
            title={voice ? "Turn voice off" : "Read this step aloud"}
          >
            {voice ? <><Volume2 className="w-3.5 h-3.5" strokeWidth={2}/> Voice on</> : <><VolumeX className="w-3.5 h-3.5" strokeWidth={1.75}/> Read aloud</>}
          </button>
        </div>
      )}

      {/* Progress bar */}
      <div className="h-1 w-full bg-[#B79256]/15">
        <div className="h-full bg-[#4E5A3D] transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>

      {/* Body */}
      {steps.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-8 text-center">
          <div>
            <ListChecks className="w-8 h-8 mx-auto text-[#B79256]" strokeWidth={1.5}/>
            <p className="font-serif-display text-3xl text-[#4E5A3D] mt-4">This branch has no steps yet.</p>
            <p className="text-[#2A2823]/60 mt-2">Cook mode needs a `## Steps` section in the recipe.</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 grid grid-rows-[1fr_auto]">
          {/* Big step text — tap left/right zones */}
          <div className="relative overflow-y-auto px-6 sm:px-16 py-10 flex items-center">
            {/* invisible tap zones for one-handed use */}
            <button
              type="button"
              onClick={() => setI((n) => Math.max(0, n - 1))}
              disabled={!canPrev}
              className="absolute inset-y-0 left-0 w-1/4 cursor-pointer disabled:cursor-default focus:outline-none"
              aria-label="Previous step"
              data-testid="cook-tap-prev"
            />
            <button
              type="button"
              onClick={() => setI((n) => Math.min(steps.length - 1, n + 1))}
              disabled={!canNext}
              className="absolute inset-y-0 right-0 w-1/4 cursor-pointer disabled:cursor-default focus:outline-none"
              aria-label="Next step"
              data-testid="cook-tap-next"
            />
            <p
              data-testid="cook-step-text"
              className="font-serif-display text-4xl sm:text-6xl leading-[1.15] text-[#2A2823] max-w-3xl mx-auto whitespace-pre-wrap"
            >
              {step}
            </p>
            {stepSeconds !== null && (
              <div className="w-full max-w-3xl mx-auto mt-8 relative z-10">
                <StepTimer
                  seconds={stepSeconds}
                  onComplete={() => {
                    if (voice && supportsSpeech) speak("Timer done. Ready for the next step.");
                  }}
                />
              </div>
            )}
          </div>

          {/* Bottom nav — big buttons for one-handed taps */}
          <div className="grid grid-cols-2 gap-3 p-4 sm:p-6 bg-white/50 border-t border-[#B79256]/25">
            <button
              type="button"
              data-testid="cook-prev-btn"
              onClick={() => setI((n) => Math.max(0, n - 1))}
              disabled={!canPrev}
              className="h-16 rounded-2xl border border-[#B79256]/40 bg-white text-[#2A2823] hover:bg-[#B79256]/12 disabled:opacity-40 disabled:hover:bg-white transition-colors inline-flex items-center justify-center gap-3 text-lg font-medium touch-manipulation active:scale-[0.98]"
            >
              <ChevronLeft className="w-6 h-6" strokeWidth={2} /> Back
            </button>
            <button
              type="button"
              data-testid="cook-next-btn"
              onClick={() =>
                canNext ? setI((n) => n + 1) : onClose?.()
              }
              className="h-16 rounded-2xl bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] transition-colors inline-flex items-center justify-center gap-3 text-lg font-medium touch-manipulation active:scale-[0.98]"
            >
              {canNext ? (
                <>Next <ChevronRight className="w-6 h-6" strokeWidth={2} /></>
              ) : (
                <>Done — thanks Mom</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
